/**
 * editorStore - in-memory model of files the user has opened in the central
 * editor. Buffers are scoped to (workspaceRoot, relativePath); opening the
 * same file twice focuses the existing buffer.
 *
 * Persistence (read/write to disk) goes through `ensureNativeApi().projects`;
 * this store only owns UI state (open list, active tab, dirty flag, layout).
 *
 * Two buffer origins share the same shape (`EditorBufferIdentity`, the
 * `origin` discriminant): `"workspace"` buffers are relative to a project's
 * workspace root, opened from the file tree, and editable. `"external"`
 * buffers are absolute paths outside any workspace root, opened from a
 * detected file-path link in chat, read through the glob-allowlisted
 * `files.preview` method instead of `projects.readFile`, and always
 * read-only — there is no write endpoint for them.
 */
import { create } from "zustand";

import type { FilePreviewResult, ProjectReadFileResult } from "@taskin-code/contracts";

import { ensureNativeApi } from "~/nativeApi";

export type CenterTabId = "chat" | { kind: "buffer"; bufferId: string };

export type EditorLayout = "tabs" | "split";

type EditorBufferIdentity =
  | { readonly origin: "workspace"; readonly relativePath: string }
  | { readonly origin: "external"; readonly path: string };

export type EditorBufferErrorKind = ProjectReadFileResult["kind"] | FilePreviewResult["kind"];

export type EditorBufferLoading = EditorBufferIdentity & {
  readonly status: "loading";
  readonly bufferId: string;
  readonly cwd: string;
};

export type EditorBufferError = EditorBufferIdentity & {
  readonly status: "error";
  readonly bufferId: string;
  readonly cwd: string;
  readonly errorMessage: string;
  readonly errorKind?: EditorBufferErrorKind;
  readonly sizeBytes?: number;
};

export type EditorBufferReady = EditorBufferIdentity & {
  readonly status: "ready";
  readonly bufferId: string;
  readonly cwd: string;
  readonly languageId: string | null;
  readonly content: string;
  readonly originalContent: string;
  readonly mtimeMs: number;
  readonly hasExternalChange: boolean;
};

export type EditorBuffer = EditorBufferLoading | EditorBufferError | EditorBufferReady;

/** What to show in a tab label / panel header, regardless of origin. */
export function displayPathOf(buffer: EditorBuffer): string {
  return buffer.origin === "workspace" ? buffer.relativePath : buffer.path;
}

interface EditorStoreState {
  readonly buffers: ReadonlyArray<EditorBuffer>;
  readonly activeTab: CenterTabId;
  readonly layout: EditorLayout;
}

export type SaveBufferOutcome =
  | { kind: "saved" }
  | { kind: "conflict" }
  | { kind: "noop" }
  | { kind: "error"; message: string };

interface EditorStoreActions {
  openFile: (input: { cwd: string; relativePath: string }) => Promise<void>;
  openExternalFile: (input: { cwd: string; path: string }) => Promise<void>;
  closeBuffer: (bufferId: string) => void;
  setActiveTab: (tab: CenterTabId) => void;
  updateContent: (bufferId: string, content: string) => void;
  saveBuffer: (bufferId: string) => Promise<SaveBufferOutcome>;
  reloadBuffer: (bufferId: string) => Promise<void>;
  markExternalChange: (input: { cwd: string; relativePath: string }) => void;
  setLayout: (layout: EditorLayout) => void;
}

export const makeBufferId = (cwd: string, relativePath: string): string =>
  `${cwd}::${relativePath}`;

export const makeExternalBufferId = (cwd: string, path: string): string =>
  `${cwd}::external::${path}`;

export const isBufferDirty = (buffer: EditorBuffer): boolean =>
  buffer.status === "ready" && buffer.content !== buffer.originalContent;

export const useEditorStore = create<EditorStoreState & EditorStoreActions>((set, get) => ({
  buffers: [],
  activeTab: "chat",
  layout: "tabs",

  setLayout(layout) {
    set({ layout });
  },

  setActiveTab(tab) {
    set({ activeTab: tab });
  },

  closeBuffer(bufferId) {
    const { buffers, activeTab } = get();
    const next = buffers.filter((buffer) => buffer.bufferId !== bufferId);
    let nextActive: CenterTabId = activeTab;
    if (typeof activeTab !== "string" && activeTab.bufferId === bufferId) {
      const remaining = next[next.length - 1];
      nextActive = remaining ? { kind: "buffer", bufferId: remaining.bufferId } : "chat";
    }
    set({ buffers: next, activeTab: nextActive });
  },

  updateContent(bufferId, content) {
    set({
      buffers: get().buffers.map((buffer) => {
        if (buffer.bufferId !== bufferId || buffer.status !== "ready") return buffer;
        return { ...buffer, content };
      }),
    });
  },

  markExternalChange({ cwd, relativePath }) {
    const targetId = makeBufferId(cwd, relativePath);
    set({
      buffers: get().buffers.map((buffer) => {
        if (buffer.bufferId !== targetId || buffer.status !== "ready") return buffer;
        return { ...buffer, hasExternalChange: true };
      }),
    });
  },

  async openFile({ cwd, relativePath }) {
    const bufferId = makeBufferId(cwd, relativePath);
    const existing = get().buffers.find((buffer) => buffer.bufferId === bufferId);
    if (existing) {
      set({ activeTab: { kind: "buffer", bufferId } });
      return;
    }

    const loading: EditorBufferLoading = {
      status: "loading",
      bufferId,
      cwd,
      origin: "workspace",
      relativePath,
    };
    set({
      buffers: [...get().buffers, loading],
      activeTab: { kind: "buffer", bufferId },
    });

    await loadFileIntoBuffer({ bufferId, cwd, relativePath, set, get });
  },

  async openExternalFile({ cwd, path }) {
    const bufferId = makeExternalBufferId(cwd, path);
    const existing = get().buffers.find((buffer) => buffer.bufferId === bufferId);
    if (existing) {
      set({ activeTab: { kind: "buffer", bufferId } });
      return;
    }

    const loading: EditorBufferLoading = {
      status: "loading",
      bufferId,
      cwd,
      origin: "external",
      path,
    };
    set({
      buffers: [...get().buffers, loading],
      activeTab: { kind: "buffer", bufferId },
    });

    await loadExternalFileIntoBuffer({ bufferId, cwd, path, set, get });
  },

  async saveBuffer(bufferId) {
    const buffer = get().buffers.find((b) => b.bufferId === bufferId);
    if (!buffer || buffer.status !== "ready" || buffer.origin !== "workspace") {
      return { kind: "noop" };
    }
    if (buffer.content === buffer.originalContent && !buffer.hasExternalChange) {
      return { kind: "noop" };
    }
    try {
      const api = ensureNativeApi();
      const result = await api.projects.writeFile({
        cwd: buffer.cwd,
        relativePath: buffer.relativePath,
        contents: buffer.content,
        baseMtimeMs: buffer.mtimeMs,
      });
      if (result.kind === "conflict") {
        set({
          buffers: get().buffers.map((b) =>
            b.bufferId === bufferId && b.status === "ready" ? { ...b, hasExternalChange: true } : b,
          ),
        });
        return { kind: "conflict" };
      }
      set({
        buffers: get().buffers.map((b) =>
          b.bufferId === bufferId && b.status === "ready"
            ? {
                ...b,
                originalContent: b.content,
                mtimeMs: result.mtimeMs,
                hasExternalChange: false,
              }
            : b,
        ),
      });
      return { kind: "saved" };
    } catch (error) {
      return {
        kind: "error",
        message: error instanceof Error ? error.message : String(error),
      };
    }
  },

  async reloadBuffer(bufferId) {
    const buffer = get().buffers.find((b) => b.bufferId === bufferId);
    if (!buffer) return;
    set({
      buffers: get().buffers.map((b): EditorBuffer => {
        if (b.bufferId !== bufferId) return b;
        return b.origin === "workspace"
          ? {
              status: "loading",
              bufferId,
              cwd: b.cwd,
              origin: "workspace",
              relativePath: b.relativePath,
            }
          : { status: "loading", bufferId, cwd: b.cwd, origin: "external", path: b.path };
      }),
    });
    if (buffer.origin === "workspace") {
      await loadFileIntoBuffer({
        bufferId,
        cwd: buffer.cwd,
        relativePath: buffer.relativePath,
        set,
        get,
      });
    } else {
      await loadExternalFileIntoBuffer({ bufferId, cwd: buffer.cwd, path: buffer.path, set, get });
    }
  },
}));

type StoreSet = (
  update: Partial<EditorStoreState> | ((state: EditorStoreState) => Partial<EditorStoreState>),
) => void;
type StoreGet = () => EditorStoreState & EditorStoreActions;

async function loadFileIntoBuffer(input: {
  readonly bufferId: string;
  readonly cwd: string;
  readonly relativePath: string;
  readonly set: StoreSet;
  readonly get: StoreGet;
}): Promise<void> {
  const { bufferId, cwd, relativePath, set, get } = input;
  try {
    const api = ensureNativeApi();
    const result = await api.projects.readFile({ cwd, relativePath });
    const current = get().buffers.find((b) => b.bufferId === bufferId);
    if (!current) return; // user closed the tab while loading

    if (result.kind === "text") {
      set({
        buffers: get().buffers.map((b) =>
          b.bufferId === bufferId
            ? {
                status: "ready",
                bufferId,
                cwd,
                origin: "workspace",
                relativePath,
                languageId: result.languageId,
                content: result.contents,
                originalContent: result.contents,
                mtimeMs: result.mtimeMs,
                hasExternalChange: false,
              }
            : b,
        ),
      });
      return;
    }

    const errorMessage =
      result.kind === "missing"
        ? "File not found."
        : result.kind === "binary"
          ? "Binary files cannot be edited here."
          : `File too large (${Math.round(result.sizeBytes / 1024)} KB; limit ${Math.round(result.maxBytes / 1024)} KB).`;

    set({
      buffers: get().buffers.map((b) =>
        b.bufferId === bufferId
          ? {
              status: "error",
              bufferId,
              cwd,
              origin: "workspace",
              relativePath,
              errorMessage,
              errorKind: result.kind,
              ...(result.kind === "binary" || result.kind === "too-large"
                ? { sizeBytes: result.sizeBytes }
                : {}),
            }
          : b,
      ),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    set({
      buffers: get().buffers.map((b) =>
        b.bufferId === bufferId
          ? {
              status: "error",
              bufferId,
              cwd,
              origin: "workspace",
              relativePath,
              errorMessage: message,
            }
          : b,
      ),
    });
  }
}

async function loadExternalFileIntoBuffer(input: {
  readonly bufferId: string;
  readonly cwd: string;
  readonly path: string;
  readonly set: StoreSet;
  readonly get: StoreGet;
}): Promise<void> {
  const { bufferId, cwd, path, set, get } = input;
  try {
    const api = ensureNativeApi();
    const result = await api.files.preview({ cwd, path });
    const current = get().buffers.find((b) => b.bufferId === bufferId);
    if (!current) return; // user closed the tab while loading

    if (result.kind === "text") {
      set({
        buffers: get().buffers.map((b) =>
          b.bufferId === bufferId
            ? {
                status: "ready",
                bufferId,
                cwd,
                origin: "external",
                path,
                languageId: result.languageId,
                content: result.contents,
                originalContent: result.contents,
                mtimeMs: result.mtimeMs,
                hasExternalChange: false,
              }
            : b,
        ),
      });
      return;
    }

    const errorMessage =
      result.kind === "missing"
        ? "File not found."
        : result.kind === "binary"
          ? "Binary files cannot be previewed here."
          : result.kind === "not-allowed"
            ? "This path isn't in the allowed preview locations."
            : `File too large (${Math.round(result.sizeBytes / 1024)} KB; limit ${Math.round(result.maxBytes / 1024)} KB).`;

    set({
      buffers: get().buffers.map((b) =>
        b.bufferId === bufferId
          ? {
              status: "error",
              bufferId,
              cwd,
              origin: "external",
              path,
              errorMessage,
              errorKind: result.kind,
              ...(result.kind === "binary" || result.kind === "too-large"
                ? { sizeBytes: result.sizeBytes }
                : {}),
            }
          : b,
      ),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    set({
      buffers: get().buffers.map((b) =>
        b.bufferId === bufferId
          ? {
              status: "error",
              bufferId,
              cwd,
              origin: "external",
              path,
              errorMessage: message,
            }
          : b,
      ),
    });
  }
}
